import { Component, ElementRef, OnInit, ViewChild, Input } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { TranslateService } from '@ngx-translate/core';
import { ROTATE, UPLOAD_IMAGE_4, UPLOAD_IMAGE_2, UPLOAD_IMAGE_3  } from 'src/defaultImages.js';

@Component({
  selector: 'app-image-editor',
  templateUrl: './image-editor.component.html',
  styleUrls: ['./image-editor.component.scss']
})
export class ImageEditorComponent implements OnInit {
  images = { 
    ROTATE: ROTATE,
    UPLOAD_IMAGE_4: UPLOAD_IMAGE_4,
    UPLOAD_IMAGE_2: UPLOAD_IMAGE_2,
    UPLOAD_IMAGE_3: UPLOAD_IMAGE_3,
  };
  @Input() data;
  modalTexts: any;

  constructor(private modalRef: NzModalRef, private sanitizer: DomSanitizer,  public translateService: TranslateService) {
    this.translateService.use(localStorage.getItem('language') || 'en_US');
   }
  file_type = "jpeg";
  canvasRotationValue = 0;
  transform = {};
  containWithinAspectRatio = false;

  enableZoomRestriction = true;

  imageGuidelines = [];

  @ViewChild('imgUpload', { static: false }) imgUpload: ElementRef;
  @ViewChild('imgPreview', { static: false }) imgPreview: ElementRef;

  public imageProps = {
    aspectRatio: null,
    maxSizeInMB: null,
    imageDimensions: ''
  }

  readonly maxUploadSizeInMB = 10;

  inputError = null;
  inputImageDimensionsConstraint = {};
  private minimumImageDimensions = {
    width: null,
    height: null,
    unit: 'px'
  }

  ngOnInit() {
    this.translateService.get('SHARED').subscribe((data: any) => {
      this.modalTexts = { ...data };
    });
    this.imageProps = {
      aspectRatio: (this.data || {}).aspectRatio || (1 / 1),
      maxSizeInMB: (this.data || {}).maxSizeInMB || 5,
      imageDimensions: (this.data || {}).imageDimensions || '500 X 500 px'
    };
    this.imageGuidelines = this.data.imageGuidelines || (
      [
        {
          img: this.images.UPLOAD_IMAGE_4,
          check: true,
          text: this.modalTexts.DISH_IN_CENTER
        },
        {
          img: this.images.UPLOAD_IMAGE_2,
          check: false,
          text: this.modalTexts.MAKE_SURE_NO_WATERMARK
        },
        {
          img: this.images.UPLOAD_IMAGE_3,
          check: false,
          text: this.modalTexts.MAKE_SURE_MUCH_SPACE
        },
        {
          img: this.images.UPLOAD_IMAGE_4,
          check: false,
          text: this.modalTexts.IMAGE_NOT_BLURRED
        },
      ]
    );
    this.minimumImageDimensions['width'] = ((this.data || {}).minimumImageDimensions || {}).width || 375;
    this.minimumImageDimensions['height'] = ((this.data || {}).minimumImageDimensions || {}).height || 312;
    let dimensions = this.imageProps.imageDimensions.toUpperCase();
    let inputDimensions = dimensions.split("X");
    this.inputImageDimensionsConstraint = {
      width: +inputDimensions[0],
      height: +(inputDimensions[1].split("px")[0])
    }
  }

  imageChangedEvent: any = '';
  croppedImage: any = '';
  imgSrc = null;
  currentImage = null;
  fileChangeEvent(event: any): void {
    this.croppedImageMeetsMinimumDimensions = true;
    this.inputError = null;
    let file = event.srcElement.files[0];
    this.currentImage = file;
    // let fileName = this.currentImage.name.split(".");
    // this.file_type = fileName[fileName.length - 1];
    if (file.size > this.maxUploadSizeInMB * 1024 * 1024) {
      this.inputError = this.modalTexts.IMAGE_SIZE_LESS + ` ${this.maxUploadSizeInMB} MB`;
      return;
    }
    this.imageChangedEvent = event;
    this.previewImage();
  }

  croppedImageMeetsMinimumDimensions = true;
  croppedImageProps = {
    width: null,
    height: null,
    unit: 'px'
  }
  imageCropped(event: ImageCroppedEvent) {
    this.croppedImage = event.base64;
    this.croppedImageProps['width'] = event.width;
    this.croppedImageProps['height'] = event.height;
    // check if ev.width and ev.height are okay, else disable upload and display error message
    if (event.width < this.minimumImageDimensions.width || event.height < this.minimumImageDimensions.height) {
      this.inputError = this.modalTexts.IMAGE_MIN + `${this.minimumImageDimensions.width} X ${this.minimumImageDimensions.height} ${this.minimumImageDimensions.unit}.`;
      this.croppedImageMeetsMinimumDimensions = false;
    }
    else {
      if(!this.inputError){
        this.croppedImageMeetsMinimumDimensions = true;
        this.inputError = null;
      }
    }
  }

  imageAvailable = false;
  imageLoaded(_image?) {
    this.imageAvailable = true;
  }

  cropperReady(_ev?) {
    // do not remove
  }


  rotateCanvas(dir) {
    if (dir === 'left') {
      this.canvasRotationValue--;
    }
    else if (dir === 'right') {
      this.canvasRotationValue++;
    }
  }

  scale = 1;
  zoomCount = 0;
  zoomCanvas(sign) {
    if (sign === '+') {
      if (this.enableZoomRestriction && this.zoomCount === 2) return;
      this.scale += .1;
      this.transform = {
        ...this.transform,
        scale: this.scale
      };
      this.enableZoomRestriction && this.zoomCount++;
    }
    else if (sign === '-') {
      if (this.enableZoomRestriction && this.zoomCount === 0) return;
      this.scale -= .1;
      this.transform = {
        ...this.transform,
        scale: this.scale
      };
      this.enableZoomRestriction && this.zoomCount--;
    }
  }

  resetImage() {
    this.scale = 1;
    this.canvasRotationValue = 0;
    this.transform = {};
  }

  browseFile() {
    let el: HTMLElement = this.imgUpload.nativeElement;
    el.click();
  }

  uploadImage() {
    let data = { image: this.croppedImage, result: 'success', file: this.dataURLToFile(this.croppedImage, 'editedImage.jpeg') }
    this.dismissModal(data);
  }

  dismissModal(data?) {
    this.modalRef.close(data);
  }

  dataURLToFile(dataURL, fileName) {
    let arr = dataURL.split(','), mime = arr[0].match(/:(.*?);/)[1], bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], fileName, { type: mime });
  }

  handleDragOver(_e) {
    if (!this.imgUpload.nativeElement.classList.contains("drop-image-zone-active")) {
      this.imgUpload.nativeElement.classList.add("drop-image-zone-active");
    }
  }

  previewImage() {
    this.imgSrc = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(this.currentImage));
  }

  previewImgLoaded(ev) {
    if (ev && ev.target) {
      const width = ev.target.naturalWidth;
      const height = ev.target.naturalHeight;
      if (width > 0 && height > 0 && width < this.minimumImageDimensions.width && height < this.minimumImageDimensions.height) {
        this.inputError = this.modalTexts.IMAGE_MIN + `${this.minimumImageDimensions.width} X ${this.minimumImageDimensions.height} ${this.minimumImageDimensions.unit}.`;
        setTimeout(() => {
          this.imageChangedEvent = null;
          this.imageAvailable = false;
        }, 100);
      }
    }
  }
}